import { useAppearance } from "@web/theme/appearance";
import {
  AppBar,
  Avatar,
  // Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  // Bell,
  LogOut,
  Moon,
  Sun,
  Menu as MenuIcon,
  // MessageSquare,
  // Search,
} from "lucide-react";
import {
  useLayoutEffect,
  useRef,
  useMemo,
  useState,
  type MouseEvent,
} from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { navigationForUser, type NavItem } from "@web/config/navigation";
import { profileImageUrl } from "@web/utils/upload-url";
import { useAuth } from "@features/auth/context/AuthContext";
import { SidebarFooter } from "./SidebarFooter";

const expandedWidth = 200;
const collapsedWidth = 64;

export function PortalLayout() {
  const theme = useTheme();
  const {
    mode,
    setMode,
    saving,
    loading,
    error: appearanceError,
  } = useAppearance();
  const dark = mode === "dark";
  const location = useLocation();
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const pageRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (reducedMotion) return;
    const animation = pageRef.current?.animate?.(
      [
        { opacity: 0.55, transform: "translateY(10px)" },
        { opacity: 1, transform: "translateY(0)" },
      ],
      { duration: 320, easing: "cubic-bezier(.2,.8,.2,1)" },
    );
    return () => animation?.cancel();
  }, [location.pathname, reducedMotion]);
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);
  const { user, logout } = useAuth();
  const items = useMemo(() => (user ? navigationForUser(user) : []), [user]);
  const sections = useMemo(() => groupNavigation(items), [items]);
  const width = collapsed ? collapsedWidth : expandedWidth;
  const currentPage =
    items.find(
      (item) =>
        item.path &&
        (location.pathname === item.path ||
          location.pathname.startsWith(`${item.path}/`)),
    )?.label || "Orbit PropList";
  const drawer = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Toolbar
        disableGutters
        sx={{ minHeight: "76px!important", justifyContent: "center", px: 0 }}
      >
        <Box
          sx={{
            position: "relative",
            width: collapsed ? 40 : 164,
            height: collapsed ? 40 : 44,
            overflow: "hidden",
            flexShrink: 0,
            borderRadius: 1,
          }}
        >
          <Box
            component="img"
            src={
              collapsed
                ? "/branding/orbit-emblem-white.png"
                : "/branding/orbit-logo-white.png"
            }
            alt="ORBIT"
            sx={{
              position: "absolute",
              width: collapsed ? 54.3 : 188,
              maxWidth: "none",
              height: "auto",
              left: collapsed ? -7.6 : -13.5,
              top: collapsed ? -9.2 : -19.1,
            }}
          />
        </Box>
      </Toolbar>
      <List
        sx={{
          px: 1,
          py: 1,
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {sections.map((section) => (
          <Box key={section.label} sx={{ mb: collapsed ? 0.25 : 0.65 }}>
            {!collapsed && section.label && (
              <Typography
                sx={{
                  px: 1.25,
                  pt: 0.5,
                  pb: 0.5,
                  color: "var(--orbit-ink-8392ac, #8392AC)",
                  fontSize: 9,
                  fontWeight: 800,
                  letterSpacing: 0.8,
                }}
              >
                {section.label}
              </Typography>
            )}
            {section.items.map((item) => (
              <ListItemButton
                key={`${section.label}-${item.label}`}
                component={item.path ? NavLink : "button"}
                to={item.path}
                onClick={() => {
                  setMobileOpen(false);
                  if (item.action === "logout") void logout();
                }}
                sx={{
                  mb: 0.25,
                  minHeight: 34,
                  borderRadius: 1,
                  color: "#D5DEED",
                  justifyContent: collapsed ? "center" : "flex-start",
                  position: "relative",
                  isolation: "isolate",
                  transition: reducedMotion
                    ? "none"
                    : "transform 180ms ease, background-color 220ms ease, box-shadow 220ms ease",
                  "& .MuiListItemIcon-root": {
                    transition: reducedMotion
                      ? "none"
                      : "transform 220ms cubic-bezier(.2,.8,.2,1), color 220ms ease",
                  },
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,.08)",
                    transform: reducedMotion ? "none" : "translateX(2px)",
                    "& .MuiListItemIcon-root": {
                      transform: reducedMotion ? "none" : "scale(1.12)",
                    },
                  },
                  "&:active": {
                    transform: reducedMotion ? "none" : "scale(.97)",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    pointerEvents: "none",
                    opacity: 0,
                    background: dark
                      ? "linear-gradient(105deg, transparent 15%, rgba(192,255,180,.35) 46%, transparent 75%)"
                      : "linear-gradient(105deg, transparent, rgba(255,231,181,.3), transparent)",
                  },
                  "@keyframes orbit-menu-shimmer": {
                    "0%": { opacity: 0, clipPath: "inset(0 100% 0 0)" },
                    "35%": { opacity: 1 },
                    "100%": { opacity: 0, clipPath: "inset(0 0 0 0)" },
                  },
                  "@keyframes orbit-menu-icon": {
                    "0%": { transform: "scale(.86)" },
                    "55%": { transform: "scale(1.16)" },
                    "100%": { transform: "scale(1)" },
                  },
                  "&.active": {
                    "&::after": {
                      animation: reducedMotion
                        ? "none"
                        : "orbit-menu-shimmer 650ms ease-out both",
                    },
                    "& .MuiListItemIcon-root": {
                      color: "#FFFFFF",
                      animation: reducedMotion
                        ? "none"
                        : "orbit-menu-icon 380ms ease-out both",
                    },
                    borderRadius: dark ? "12px" : undefined,
                    background: dark
                      ? "linear-gradient(90deg, rgba(154,255,82,.92) 0%, rgba(71,207,83,.48) 7%, rgba(22,79,51,.62) 26%, rgba(12,35,30,.60) 72%, rgba(124,190,160,.26) 100%)"
                      : "linear-gradient(90deg, rgba(170,132,68,.9), rgba(153,128,83,.68))",
                    boxShadow: dark
                      ? "inset 2px 0 3px #c6ff9c, inset 0 1px 2px rgba(212,255,224,.48), inset -3px 0 9px rgba(186,244,212,.32), -7px 0 22px rgba(129,255,63,.42), 0 0 12px rgba(107,235,130,.16)"
                      : "none",
                    border: dark
                      ? "1px solid rgba(184,244,200,.55)"
                      : "1px solid transparent",
                    color: "#FFFFFF",
                  },
                }}
              >
                <Tooltip title={collapsed ? item.label : ""} placement="right">
                  <ListItemIcon
                    sx={{
                      minWidth: collapsed ? 0 : 32,
                      color: "#B8C5DB",
                      "& svg": { width: 17, height: 17 },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                </Tooltip>
                {!collapsed && (
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 12, fontWeight: 650 }}
                  />
                )}
              </ListItemButton>
            ))}
          </Box>
        ))}
      </List>
      <SidebarFooter collapsed={collapsed} />
    </Box>
  );
  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        "--portal-content-height": "calc(100vh - 98px)",
        "@media (max-height: 700px)": {
          "--portal-content-height": "calc(100vh - 82px)",
        },
      }}
    >
      <AppBar
        position="fixed"
        color="inherit"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: dark ? "rgba(12,25,36,.48)" : "background.paper",
          ml: { md: `${width}px` },
          width: { md: `calc(100% - ${width}px)` },
          transition: reducedMotion
            ? "none"
            : theme.transitions.create(["width", "margin"]),
        }}
      >
        <Toolbar sx={{ minHeight: "58px!important", gap: 0.75 }}>
          <IconButton
            onClick={() =>
              desktop ? setCollapsed(!collapsed) : setMobileOpen(true)
            }
            aria-label="Toggle navigation"
          >
            <MenuIcon size={19} />
          </IconButton>
          <Typography fontSize={14} fontWeight={800} sx={{ mr: "auto" }}>
            {currentPage}
          </Typography>
          <Tooltip
            describeChild
            title={
              appearanceError ||
              (dark ? "Switch to light theme" : "Switch to dark glass theme")
            }
          >
            <span>
              <IconButton
                aria-label={
                  dark ? "Switch to light theme" : "Switch to dark glass theme"
                }
                disabled={saving || loading}
                onClick={() => setMode(dark ? "light" : "dark")}
              >
                {dark ? <Sun size={19} /> : <Moon size={19} />}
              </IconButton>
            </span>
          </Tooltip>
          {appearanceError && (
            <Typography role="status" color="error" variant="caption">
              Appearance could not be saved
            </Typography>
          )}
          {/* <Tooltip title="Search leads">
            <IconButton
              aria-label="Focus lead search"
              onClick={() =>
                document
                  .querySelector<HTMLInputElement>(
                    '[aria-label="Search leads"]',
                  )
                  ?.focus()
              }
            >
              <Search size={19} />
            </IconButton>
          </Tooltip>
          <IconButton aria-label="Notifications">
            <Badge badgeContent={0} color="error">
              <Bell size={20} />
            </Badge>
          </IconButton>
          <Tooltip title="Messages">
            <IconButton
              aria-label="Messages"
              sx={{ display: { xs: "none", lg: "inline-flex" } }}
            >
              <MessageSquare size={19} />
            </IconButton>
          </Tooltip> */}
          <IconButton
            onClick={(event: MouseEvent<HTMLElement>) =>
              setProfileAnchor(event.currentTarget)
            }
            aria-label="Open profile menu"
          >
            <Avatar
              src={profileImageUrl(user?.profileImageUrl)}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "primary.main",
                fontSize: 14,
              }}
            >
              {user?.name
                ?.split(" ")
                .map((part) => part[0])
                .slice(0, 2)
                .join("")}
            </Avatar>
          </IconButton>
          <Box sx={{ display: { xs: "none", lg: "block" }, mr: 0.5 }}>
            <Typography fontSize={13} fontWeight={800} lineHeight={1.2}>
              {user?.name}
            </Typography>
            <Typography fontSize={11} color="text.secondary">
              {user?.designation || "Designation not set"}
            </Typography>
          </Box>
          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
          >
            <MenuItem component={NavLink} to="/profile">
              My profile
            </MenuItem>
            <MenuItem onClick={() => void logout()}>
              <LogOut size={16} />
              &nbsp; Sign out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
      <Drawer
        variant="permanent"
        open
        sx={{
          display: { xs: "none", md: "block" },
          width,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width,
            bgcolor: dark ? "rgba(10,22,32,.48)" : "#07162F",
            borderRight: dark ? "1px solid rgba(185,218,242,.42)" : 0,
            color: "white",
            transition: reducedMotion
              ? "none"
              : theme.transitions.create("width"),
            overflowX: "hidden",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: expandedWidth,
            bgcolor: dark ? "rgba(10,22,32,.48)" : "#07162F",
            borderRight: dark ? "1px solid rgba(185,218,242,.42)" : 0,
            color: "white",
          },
        }}
      >
        {drawer}
      </Drawer>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pt: "58px",
          width: { md: `calc(100% - ${width}px)` },
          transition: reducedMotion
            ? "none"
            : theme.transitions.create("width"),
        }}
      >
        <Box
          ref={pageRef}
          sx={{
            p: { xs: 1.5, sm: 2, lg: 2.5 },
            maxWidth: 1680,
            mx: "auto",
            "@media (max-height: 700px)": {
              p: 1.5,
            },
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

function groupNavigation(items: NavItem[]) {
  return items.reduce<{ label: string; items: NavItem[] }[]>((groups, item) => {
    const section = groups.find((group) => group.label === item.section);
    if (section) section.items.push(item);
    else groups.push({ label: item.section, items: [item] });
    return groups;
  }, []);
}
